import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, RotateCcw } from '../../icons';
// 채택 3단계 ② 인사 — 화면 머리·표·팝업·입력을 정본 공통으로(A3 · E1 · C1 · B1).
import {
  Button, Checkbox, FormField, Input, Modal, ModalActions,
  PageHeader, Select, Table, type TableColumn,
} from '../../components/shared';
import {
  fetchJobRoles, fetchRoleTemplate, createJobRole, updateJobRole, adoptRoleTemplate,
  SCOPE_LABEL, type JobRoleDto, type RoleTemplateItem, type RoleScope,
} from '../../api/hr-job-roles';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import '../../styles/master-screen.css';
import './JobRolesPage.css';

/**
 * 직책 마스터 (인사, migration 144 · 2026-08-29 / 기준 화면 2026-09-02).
 *
 * ★★ **직위·직급과 «다른 화면»이어야 한다.** 한 화면에 섞으면 둘이 다시 `position` 으로
 *    뭉개진다 — 영문 이름이 같아 섞이는 것이 이 문제의 근본 원인이었다.
 *    · 직위(rank) — 서열. **승진**으로 오른다. 급여 등급과 이어진다. 반드시 있다
 *    · 직책(role) — 조직에 대한 **보임**. **발령**으로 부여되고 결재권을 수반한다.
 *      없을 수 있고 **동시에 여럿일 수 있다**(겸직)
 *
 * ★ **급은 직위 마스터와 같은 뜻**(클수록 높다) — 두 축을 견줄 수 있어야 「과장인데 팀장」이
 *   「차장인데 팀원」보다 위라고 말할 수 있다.
 * ★ **지우지 않고 중지한다** — 지난 보임이 그 이름을 가리킨다.
 *
 * ── 2026-09-02 · 기준 화면(정본 P3-1) ────────────────────────────────────
 * 회사의 기준을 만들고 목록으로 훑는 화면은 생김새가 같다. 직위·직급이 먼저 이 배치를
 * 세웠고(#920) 여기가 두 번째다.
 *   · 화면 머리 = 제목 + 도움말 + 요약 칩 + 대표 행동 1 (`PageHeader`)
 *   · 왼쪽 264px = **담을 것**(표준 묶음)  · 오른쪽 = **본표**(우리 회사의 직책)
 *   · 등록 · 수정 = **팝업**. 표 아래 넣기 줄을 상주시키지 않는다
 * ★★★ **수정이 아예 없었다.** 서버에는 PATCH 가 있는데(이름·호칭·급·범위) 화면이 부르지
 *    않아, 한 글자만 틀려도 **고칠 방법이 없었다** — 직위·직급에서 겪은 것과 같은 구멍이다.
 * ★★ **말을 정본 P7 로 맞췄다** — 「담기」→**등록**, 「접기/펴기」→**중지/사용**,
 *    「만들기」→**등록**(회사 마스터에 넣는 일이라 «만들기» 가 아니다).
 */

const SCOPES: RoleScope[] = ['TEAM', 'DIVISION', 'COMPANY'];
const SCOPE_OPTIONS = SCOPES.map((s) => ({ value: s, label: SCOPE_LABEL[s] }));

const emptyForm = () => ({ name: '', callName: '', level: '', scopeKind: 'TEAM' as RoleScope, isHead: false });
type RoleForm = ReturnType<typeof emptyForm>;

/** 열려 있는 팝업 — 등록·수정은 한 폼을 쓴다(`editing` 이 있으면 수정). */
type OpenModal = { kind: 'role' } | { kind: 'adopt' };

export function JobRolesPage() {
  const [rows, setRows] = useState<JobRoleDto[] | null>(null);
  const [tpl, setTpl] = useState<RoleTemplateItem[]>([]);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');
  const [form, setForm] = useState<RoleForm>(emptyForm);
  const [editing, setEditing] = useState<JobRoleDto | null>(null);
  const [modal, setModal] = useState<OpenModal | null>(null);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [list, items] = await Promise.all([
      fetchJobRoles(true),
      fetchRoleTemplate().catch(() => [] as RoleTemplateItem[]),
    ]);
    setRows(list);
    setTpl(items);
  }, []);

  useEffect(() => {
    let alive = true;
    load()
      .then(() => { if (alive) setState('ok'); })
      // 옛 서버는 이 라우트를 모른다(404) — 화면이 그 사실을 말하고 접힌다.
      .catch(() => { if (alive) setState('folded'); });
    return () => { alive = false; };
  }, [load]);

  const run = async (tag: string, fn: () => Promise<void>, fail: string) => {
    setBusy(tag); setError(null); setNotice(null);
    try { await fn(); await load(); }
    catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setError(status === 403 ? '고칠 권한이 없습니다 — 인사 담당자에게 부탁하세요.'
        : status === 409 ? '이미 있는 직책입니다.' : fail);
    }
    finally { setBusy(null); }
  };

  /** 등록 · 수정 — 한 폼이 두 일을 한다. 무엇을 하는 중인지는 `editing` 이 가른다. */
  const submit = () => run('save', async () => {
    const name = form.name.trim();
    if (!name) { setError('직책 이름을 적어 주세요.'); return; }
    const body = {
      name,
      callName: form.callName.trim() || null,
      level: form.level ? Number(form.level) : undefined,
      scopeKind: form.scopeKind,
      isHead: form.isHead,
    };
    if (editing) {
      await updateJobRole(editing.id, { ...body, level: body.level ?? 0 });
      setNotice(`«${name}» 을 저장했습니다.`);
    } else {
      await createJobRole(body);
      setNotice(`«${name}» 을 등록했습니다.`);
    }
    setModal(null); setEditing(null); setForm(emptyForm());
  }, editing ? '저장하지 못했습니다.' : '등록하지 못했습니다.');

  /**
   * ★★ **담고 나서 무엇이 됐는지 말한다.** 눌렀는데 아무 말이 없으면 됐는지 알 수 없다
   *    (내 서명 화면에서 겪은 것과 같은 이유).
   * ★ 골라 담는다 — 표준 묶음을 통째로 밀어 넣지 않는다(회사가 안 쓰는 자리가 있다).
   */
  const adopt = () => run('adopt', async () => {
    const names = [...picked];
    const r = await adoptRoleTemplate(names.length > 0 ? names : undefined);
    setNotice(r.adopted.length === 0
      ? '이미 다 있습니다 — 보탤 것이 없었습니다.'
      : `${r.adopted.length}개를 등록했습니다: ${r.adopted.join(' · ')}`
        + (r.skipped.length > 0 ? ` (이미 있던 ${r.skipped.length}개는 그대로 두었습니다)` : ''));
    setModal(null); setPicked(new Set());
  }, '등록하지 못했습니다.');

  /** 결재권 — 참이면 보임할 때 그 조직의 장으로 세워진다(파트장·TF장은 거짓). */
  const head = (r: JobRoleDto) => run(`h${r.id}`, async () => {
    await updateJobRole(r.id, { isHead: !r.isHead });
    setNotice(r.isHead
      ? `«${r.name}» 은 이제 결재 사다리에 서지 않습니다.`
      : `«${r.name}» 을 보임하면 그 조직의 장으로 섭니다.`);
  }, '바꾸지 못했습니다.');

  const toggle = (r: JobRoleDto) => run(`t${r.id}`, async () => {
    await updateJobRole(r.id, { active: !r.active });
    setNotice(r.active
      ? `«${r.name}» 을 중지했습니다 — 새로 보임할 수 없습니다.`
      : `«${r.name}» 을 다시 사용합니다.`);
  }, '바꾸지 못했습니다.');

  const startNew = () => {
    setEditing(null); setForm(emptyForm()); setError(null); setModal({ kind: 'role' });
  };
  const startEdit = (r: JobRoleDto) => {
    setEditing(r);
    setForm({
      name: r.name, callName: r.callName ?? '', level: String(r.level),
      scopeKind: r.scopeKind, isHead: r.isHead,
    });
    setError(null); setModal({ kind: 'role' });
  };

  /** 표준 묶음 — 이미 있는 이름은 «있음» 으로 지워 둔다(다시 담을 것이 없다). */
  const tplMarked = useMemo(
    () => tpl.map((t) => ({ ...t, taken: (rows ?? []).some((r) => r.name === t.name) })),
    [tpl, rows],
  );
  const fresh = tplMarked.filter((t) => !t.taken);
  const pickedFresh = fresh.filter((t) => picked.has(t.name));

  const list = rows ?? [];

  const columns: ReadonlyArray<TableColumn<JobRoleDto>> = [
    {
      key: 'name', header: '직책',
      render: (r) => (
        <b className={`hrrole__nm${r.active ? '' : ' hrrole__nm--off'}`}>{r.name}</b>
      ),
    },
    { key: 'callName', header: '호칭', render: (r) => r.callName || '—' },
    { key: 'level', header: '급', numeric: true },
    { key: 'scopeKind', header: '범위', render: (r) => SCOPE_LABEL[r.scopeKind] },
    {
      /*
        ★★ **이 값이 결재권을 가른다** — 참이면 보임할 때 그 조직의 장으로 세워져
           결재 사다리에 선다. 안 보이면 아무도 고칠 수 없으므로 표에 드러낸다.
      */
      key: 'isHead', header: '결재권',
      render: (r) => (
        <Checkbox
          checked={r.isHead}
          disabled={busy === `h${r.id}` || !r.active}
          /* ★ 이름에 «누구의» 를 넣는다 — 같은 이름의 체크칸이 일곱 줄 나란히 서면
                어느 것인지 알 수 없다. 보이는 글(그 조직의 장)을 이름 안에 품는다. */
          aria-label={`${r.name} 결재권 — 그 조직의 장`}
          onChange={() => head(r)}
          label="그 조직의 장"
        />
      ),
    },
    {
      key: 'active', header: '상태',
      render: (r) => (
        <span className={`wc-master__st${r.active ? ' is-on' : ''}`}>{r.active ? '사용' : '중지'}</span>
      ),
    },
    {
      /* ★ 행 작업은 «늘 보이게» 둔다 — `rowActions` 는 hover 로만 나타난다(정본 P5 위반). */
      key: 'act', header: <span className="wc-master__sr">작업</span>, align: 'right',
      render: (r) => (
        <span className="hrrole__acts">
          <Button
            variant="secondary" size="sm"
            aria-label={`${r.name} 수정`}
            onClick={() => startEdit(r)}
            iconLeft={<Pencil size={13} aria-hidden />}
          >
            수정
          </Button>
          {r.active ? (
            <Button
              variant="quiet-danger" size="sm"
              aria-label={`${r.name} 중지`}
              disabled={busy === `t${r.id}`}
              onClick={() => toggle(r)}
            >
              중지
            </Button>
          ) : (
            <Button
              variant="secondary" size="sm"
              aria-label={`${r.name} 사용`}
              disabled={busy === `t${r.id}`}
              onClick={() => toggle(r)}
              iconLeft={<RotateCcw size={13} aria-hidden />}
            >
              사용
            </Button>
          )}
        </span>
      ),
    },
  ];

  if (state === 'loading') return <div className="hrpage__empty">불러오는 중…</div>;

  return (
    <div className="hrpage hrrole">
      {/* ── 화면 머리 — 제목 · 도움말 · 요약 · 대표 행동 (정본 P3-1 · A3) ────
             ★ 설명문을 화면에 두지 않는다. 「원래 그런 규칙」은 도움말이 맡는다(정본 P8). */}
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/job-roles')}
        scope={scopeFor('/erp/hr/job-roles')}
        title="직책"
        help={{
          title: '직책',
          body: (
            <>
              · <b>직책은 직위와 다른 축입니다.</b> 직위는 <b>승진</b>으로 오르는 서열이고,
              직책은 조직에 대한 <b>보임</b>입니다 — 없을 수도, 동시에 여럿일 수도 있습니다(겸직).<br />
              · <b>급</b>은 직위와 같은 뜻(클수록 높음)이라 「과장인데 팀장」이 「차장인데 팀원」보다 위입니다.<br />
              · <b>결재권</b>이 켜진 직책을 보임하면 그 조직의 장으로 서서 결재 사다리에 오릅니다 —
              파트장·TF장처럼 결재권이 없는 자리는 꺼 둡니다.<br />
              · 지우지 않고 <b>중지</b>합니다. 중지한 직책에는 새로 보임할 수 없고,
              이미 맡고 있는 사람의 기록은 그대로 남습니다.
            </>
          ),
        }}
        actions={
          <>
            {/* 2026-10-14 사용자 지시 — 화면 머리 오른쪽의 건수(사용 중 · 결재권)를 지웠다(메일과 같은 규칙). */}
            <Button
              variant="primary" size="compact" onClick={startNew}
              iconLeft={<Plus size={14} aria-hidden />}
            >
              직책 등록
            </Button>
          </>
        }
      />

      {state === 'folded' && <div className="hrpage__empty">직책 마스터를 아직 쓸 수 없습니다.</div>}

      {state !== 'folded' && (
        <>
          {/* ★ 팝업이 열려 있으면 오류는 «그 안에서만» 말한다 — 두 곳에 같은 문장이 서면
                 읽어 주는 쪽이 같은 오류를 두 번 듣는다. */}
          {error && !modal && <p className="wc-master__err" role="alert">{error}</p>}
          {notice && !error && <p className="wc-master__ok" role="status">{notice}</p>}

          <div className="wc-master__two">
            {/* ── 왼쪽 — 담을 것 ── */}
            <div className="wc-master__side">
              <section className="wc-master__panel" aria-label="표준 묶음">
                <div className="wc-master__panelh">
                  <span className="wc-master__panelt">표준 묶음</span>
                  <span className="wc-master__paneln">골라서 등록한다</span>
                </div>
                <div className="wc-master__panelb">
                  {tplMarked.length === 0 ? (
                    <p className="hrpage__empty">표준 묶음을 불러오지 못했습니다.</p>
                  ) : (
                    <>
                      <ul className="wc-master__pick">
                        {tplMarked.map((t) => {
                          const on = picked.has(t.name);
                          return (
                            <li key={t.name} className={t.taken ? 'is-taken' : (on ? 'is-on' : undefined)}>
                              {/* ★ `Checkbox` 가 스스로 `<label>` 이다 — 그 밖을 라벨로 또 감싸면
                                     라벨이 겹쳐 읽어 주는 쪽이 무엇을 고르는지 잃는다.
                                     줄 전체(이름·있음·급)를 그 라벨 «안» 에 넣는다. */}
                              <Checkbox
                                className="wc-master__pickrow"
                                checked={on} disabled={t.taken}
                                onChange={() => setPicked((p) => {
                                  const next = new Set(p);
                                  if (next.has(t.name)) next.delete(t.name); else next.add(t.name);
                                  return next;
                                })}
                                label={(
                                  <>
                                    <span className="wc-master__pickname">{t.name}</span>
                                    {t.taken && <span className="wc-master__have">있음</span>}
                                    <span className="wc-master__picksub">{SCOPE_LABEL[t.scopeKind]} · {t.level}</span>
                                  </>
                                )}
                              />
                            </li>
                          );
                        })}
                      </ul>
                      <div className="wc-master__pickfoot">
                        <Button
                          variant="secondary" size="compact"
                          disabled={pickedFresh.length === 0 || busy === 'adopt'}
                          onClick={() => { setError(null); setModal({ kind: 'adopt' }); }}
                        >
                          {pickedFresh.length > 0 ? `${pickedFresh.length}개 등록` : '등록'}
                        </Button>
                        {fresh.length > 0 && (
                          <Button
                            variant="ghost" size="compact"
                            onClick={() => setPicked(new Set(fresh.map((t) => t.name)))}
                          >
                            모두 고르기
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </section>
            </div>

            {/* ── 오른쪽 — 본표 ── */}
            <section className="wc-master__panel wc-master__main" aria-label="직책표">
              <div className="wc-master__panelh">
                <span className="wc-master__panelt">직책표</span>
                <span className="wc-master__paneln">결재권이 켜진 자리가 결재 사다리에 선다</span>
              </div>
              <Table
                caption="직책표"
                className="hrrole__tbl"
                columns={columns}
                rows={list}
                rowKey={(r) => r.id}
                empty={(
                  <>왼쪽 <b>표준 묶음</b>에서 골라 등록하거나, 오른쪽 위 <b>직책 등록</b>으로 시작합니다.</>
                )}
              />
            </section>
          </div>
        </>
      )}

      {/* ── 등록 · 수정 팝업 ── */}
      {modal?.kind === 'role' && (
        <Modal
          size="sm"
          title={editing ? `직책 수정 — ${editing.name}` : '직책 등록'}
          onClose={() => { setModal(null); setEditing(null); }}
          footer={(
            <ModalActions>
              <Button variant="secondary" onClick={() => { setModal(null); setEditing(null); }}>취소</Button>
              <Button variant="primary" onClick={submit} disabled={busy === 'save'}>
                {editing ? '저장' : '등록'}
              </Button>
            </ModalActions>
          )}
        >
          {error && <p className="wc-master__err" role="alert">{error}</p>}
          <div className="wc-master__form">
            {/* ★ 한 줄에 선 칸은 «도움말 유무»로 높이가 갈린다 — 셋 중 둘만 도움말이 있으면
                   입력 상자가 18px 어긋나 선다(운영 육안에서 잡았다). 이름 칸에도 제 몫의
                   한 줄을 준다: 이 이름이 «어디에» 나오는지가 실제로 궁금한 값이다. */}
            <FormField label="직책 이름" required hint="인사발령의 «보임»에서 고르는 이름">
              <Input
                value={form.name} autoFocus
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </FormField>
            <FormField label="호칭" hint="비우면 이름이 곧 호칭입니다">
              <Input
                value={form.callName}
                onChange={(e) => setForm({ ...form, callName: e.target.value })}
              />
            </FormField>
            <FormField label="급" hint="클수록 높다" className="wc-master__narrow">
              <Input
                value={form.level} inputMode="numeric"
                onChange={(e) => setForm({ ...form, level: e.target.value.replace(/[^0-9]/g, '') })}
              />
            </FormField>
            <FormField label="범위">
              <Select
                value={form.scopeKind}
                options={SCOPE_OPTIONS}
                onChange={(v) => setForm({ ...form, scopeKind: v as RoleScope })}
              />
            </FormField>
            <div className="wc-master__wide">
              <Checkbox
                checked={form.isHead}
                label="그 조직의 장 — 보임하면 결재 사다리에 선다"
                onChange={(e) => setForm({ ...form, isHead: e.target.checked })}
              />
            </div>
          </div>
        </Modal>
      )}

      {/* ── 표준 묶음 등록 확인 ── */}
      {modal?.kind === 'adopt' && (
        <Modal
          size="sm"
          title="표준 묶음 등록"
          onClose={() => setModal(null)}
          footer={(
            <ModalActions>
              <Button variant="secondary" onClick={() => setModal(null)}>취소</Button>
              <Button variant="primary" onClick={adopt} disabled={busy === 'adopt'}>등록</Button>
            </ModalActions>
          )}
        >
          {error && <p className="wc-master__err" role="alert">{error}</p>}
          <p className="wc-master__mlead">{pickedFresh.length}개를 직책표에 등록합니다.</p>
          <ul className="wc-master__mlist">
            {pickedFresh.map((t) => (
              <li key={t.name}>
                <b>{t.name}</b>
                <span className="wc-master__picksub">{SCOPE_LABEL[t.scopeKind]} · 급 {t.level}</span>
                <span className="wc-master__sp" />
                {t.isHead && <span className="wc-master__st is-on">결재권</span>}
              </li>
            ))}
          </ul>
          <p className="wc-master__mnote">
            <b>이미 있는 이름은 건너뜁니다.</b> 회사가 고쳐 둔 급·범위를 표준값으로 되돌리지 않습니다.
          </p>
        </Modal>
      )}
    </div>
  );
}
